import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, User as UserIcon, Loader2, Check } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import LogoDisplay from "../../config/LogoDisplay";

export default function SignUpPage() {
  const { user, signUpWithEmail, signInWithGoogle } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/dashboard";
  const website = params.get("website");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Forward to the destination once authentication completes. Must NOT live in
  // the click handlers: the Google *redirect* flow (used as a fallback when the
  // popup is blocked, on any platform) does a full-page round-trip to Google
  // and returns to /signup on a fresh load — the code after `await
  // signInWithGoogle()` never runs, and `getRedirectResult` in AuthContext
  // signs the user in out-of-band. Without this, the now-authenticated user
  // lands back on the sign-up page and appears stuck in a loop. Watching `user`
  // also covers the popup path and email sign-up; `replace` keeps the auth page
  // out of history.
  useEffect(() => {
    if (user) nav(redirect, { replace: true });
  }, [user, redirect, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Name is required.");
    if (form.password.length < 8) return setErr("Password must be 8+ characters.");
    setLoading(true);
    try {
      await signUpWithEmail(form.email.trim(), form.password, {
        displayName: form.name.trim(),
      });
    } catch (e) {
      setErr(prettify(e));
    } finally {
      setLoading(false);
    }
  };

  const google = async () => {
    setErr("");
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (e) {
      setErr(prettify(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#0B1038] flex flex-col font-sans">
      <div className="px-6 py-5">
        <LogoDisplay size="md" showWordmark={false} />
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-10">
        <div className="grid lg:grid-cols-2 gap-10 max-w-5xl w-full items-center">
          <div className="hidden lg:block">
            <h2 className="font-display text-4xl text-[#0B1038] tracking-tight leading-tight">
              Find leads that{" "}
              <span className="text-blue-600">actually want</span> to hear from you.
            </h2>
            <ul className="mt-6 space-y-3 text-sm text-gray-700">
              {[
                "AI-generated lead campaigns",
                "X (Twitter), LinkedIn & Reddit intent monitoring",
                "Brand-voice reply drafts",
                "Always human-reviewed",
              ].map((x) => (
                <li key={x} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-blue-600" />
                  {x}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-md p-6 sm:p-8 shadow-sm">
            <h1 className="font-display text-3xl text-[#0B1038] tracking-tight leading-tight">
              Create your account.
            </h1>
            <p className="text-sm text-gray-600 mt-2">
              {website ? (
                <>Create an account to analyze <span className="text-blue-600 font-medium">{website}</span> and find your first leads.</>
              ) : (
                <>Start with <span className="text-blue-600 font-medium">50 free credits</span>.</>
              )}
            </p>
            {err && (
              <div className="mt-4 px-3 py-2 rounded-sm bg-rose-50 border border-rose-200 text-rose-700 text-sm">
                {err}
              </div>
            )}
            <form onSubmit={submit} className="mt-6 space-y-3">
              <Input
                Icon={UserIcon}
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <Input
                Icon={Mail}
                type="email"
                placeholder="Work email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <Input
                Icon={Lock}
                type="password"
                placeholder="Password (8+ chars)"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <button
                disabled={loading}
                className="w-full inline-flex justify-center items-center gap-2 bg-[#0B1038] text-white font-semibold py-2.5 rounded-sm hover:bg-[#151c54] disabled:opacity-60 transition-colors"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Create account
              </button>
              <button
                type="button"
                onClick={google}
                disabled={loading}
                className="w-full text-sm font-semibold py-2.5 rounded-sm border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Continue with Google
              </button>
            </form>
            <p className="mt-5 text-xs text-gray-500">
              By creating an account you agree to our terms. We'll send a
              verification email — please confirm to unlock all features.
            </p>
            <div className="mt-4 text-sm text-gray-600 text-center">
              Already have an account?{" "}
              <Link
                to={`/login${redirect !== "/dashboard" ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}
                className="font-semibold text-blue-600 hover:underline"
              >
                Log in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ Icon, ...props }) {
  return (
    <div className="relative">
      {Icon && (
        <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      )}
      <input
        {...props}
        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-shadow"
      />
    </div>
  );
}

function prettify(err) {
  if (!err) return "Something went wrong.";
  if (err.code) {
    return err.code
      .replace("auth/", "")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return err.message || "Something went wrong.";
}
